import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { base64Encode, base64Decode, base62ToDecimal } from '../utils/crypto';
import { userLogin, userLogout, getWalletBalance, loadCachedBasicData, clearCachedBasicData } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [currentMode, setCurrentMode] = useState('sc');
    const [isLoading, setIsLoading] = useState(true);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);

    // Load user from localStorage on mount
    useEffect(() => {
        const loadUser = () => {
            try {
                // Load cached basic data first
                loadCachedBasicData();
                
                const encryptedUser = localStorage.getItem('winburst_user');
                if (encryptedUser) {
                    const decryptedUser = JSON.parse(base64Decode(encryptedUser));
                    setUser(decryptedUser);
                } else {
                    setShowLoginModal(true);
                }
            } catch (error) {
                console.error('Failed to load user:', error);
                setShowLoginModal(true);
            }
            setIsLoading(false);
        };

        loadUser();
    }, []);



    // Save user to localStorage (encrypted)
    const saveUser = useCallback((userData) => {
        const encryptedUser = base64Encode(JSON.stringify(userData));
        localStorage.setItem('winburst_user', encryptedUser);
    }, []);

    // Clear user from localStorage
    const clearUser = useCallback(() => {
        localStorage.removeItem('winburst_user');
        clearCachedBasicData();
        setUser(null);
        setWallet(null);
    }, []);

    // Login success callback - can be set by components
    const onLoginSuccessCallbackRef = useRef(null);
    const setOnLoginSuccess = useCallback((callback) => {
        onLoginSuccessCallbackRef.current = callback;
    }, []);

    // Login function
    const login = useCallback(async (email, password) => {
        try {
            const response = await userLogin(email, password);
            
            if (response.user_info && response.user_token) {
                // Convert user_id from base62 to decimal for s_user_id
                const userInfo = {
                    ...response.user_info,
                    user_token: response.user_token,
                    s_user_id: base62ToDecimal(response.user_info.user_id)
                };
                
                // Save encrypted user data
                saveUser(userInfo);
                setUser(userInfo);
                
                // Fetch wallet after login
                await fetchWallet(userInfo.user_id);
                
                setShowLoginModal(false);
                
                // Trigger login success callback
                if (onLoginSuccessCallbackRef.current) {
                    onLoginSuccessCallbackRef.current(userInfo);
                }
                
                return { success: true, user: userInfo };
            } else {
                throw new Error('Invalid login response');
            }
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, error: error.message };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveUser]);

    // Logout function
    const logout = useCallback(async () => {
        try {
            if (user && user.user_id) {
                await userLogout(user.user_id);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            clearUser();
            setShowLoginModal(true);
        }
    }, [user, clearUser]);

    // Fetch wallet balance
    const fetchWallet = useCallback(async (userId) => {
        try {
            const walletData = await getWalletBalance(userId);
            setWallet(walletData);
            return walletData;
        } catch (error) {
            console.error('Fetch wallet error:', error);
            throw error;
        }
    }, []);

    // Refresh wallet (called from components)
    const refreshWallet = useCallback(async () => {
        if (user && user.user_id) {
            return await fetchWallet(user.user_id);
        }
    }, [user, fetchWallet]);

    // Calculate SC (ts_money + rd_money + gm_money)
    const getSCBalance = useCallback(() => {
        if (!wallet) return 0;
        return (wallet.ts_money || 0) + (wallet.rd_money || 0) + (wallet.gm_money || 0);
    }, [wallet]);

    // Calculate GC (coin)
    const getGCBalance = useCallback(() => {
        if (!wallet) return 0;
        return wallet.coin || 0;
    }, [wallet]);

    // Mode switching
    const switchMode = useCallback((mode) => {
        setCurrentMode(mode);
        localStorage.setItem('winburst-mode', mode);
    }, []);

    // Get user info for display
    const getUserInfo = useCallback(() => {
        if (!user) return null;
        return {
            userId: user.user_id,
            nickname: user.nickname || 'Unknown',
            vipLevel: user.vip_level || 0,
            sUserId: user.s_user_id || 'N/A'
        };
    }, [user]);

    const value = {
        user,
        wallet,
        currentMode,
        isLoading,
        showLoginModal,
        showUserInfoModal,
        setShowLoginModal,
        setShowUserInfoModal,
        login,
        logout,
        refreshWallet,
        getSCBalance,
        getGCBalance,
        getUserInfo,
        switchMode,
        setOnLoginSuccess
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
